import {
  PiniaPlugin,
  PiniaPluginContext,
  StateTree,
  Store,
  SubscriptionCallbackMutation,
} from 'pinia';
import localForage from 'localforage';

import logger from '@/services/logger';
import { State } from './state';

declare module 'pinia' {
  export interface PiniaCustomProperties {
    /**
     * Rehydrates store from persisted state
     */
    $hydrate: () => Promise<void>;

    /**
     * Persists store into configured storage
     */
    $persist: () => Promise<void>;
  }
}

const hydrateState = async (store: Store) => {
  try {
    const stored: State = (await localForage.getItem(
      store.$id + '-state',
    )) as State;
    if (stored) {
      store.$patch(stored as object);
    }
  } catch (err: any) {
    logger.error('hydrateState', { error: err.message ?? err });
  }
};

const persistState = async (store: Store, state: StateTree) => {
  try {
    await localForage.setItem(
      store.$id + '-state',
      JSON.parse(JSON.stringify(state)),
    );
  } catch (err: any) {
    logger.error('persistState', { error: err.message ?? err });
  }
};

export const createLocalForagePlugin = (): PiniaPlugin => {
  return (context: PiniaPluginContext) => {
    const { store } = context;

    store.$persist = async () => {
      await persistState(store, store.$state);
    };

    store.$hydrate = async () => {
      await hydrateState(store);
    };

    hydrateState(store).catch((err: any) =>
      logger.error('Pinia plugin error while hydrating', {
        error: err.message ?? err,
      }),
    );

    store.$subscribe(
      (
        _mutation: SubscriptionCallbackMutation<StateTree>,
        state: StateTree,
      ) => {
        persistState(store, state).catch((err: any) =>
          logger.error('Pinia plugin error while persisting', {
            error: err.message ?? err,
          }),
        );
      },
      {
        detached: true,
      },
    );
  };
};
